import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { SelectionItem } from '../selections/selection-item';
import { PersonLoginService } from '../services/person-login-service';
import { SelectionService } from '../services/selection-service';
import { TaskDelegationService } from '../services/task-delegation-service';
import { DateUtils } from '../utils/date-utils';
import { StringUtils } from '../utils/string-utils';
import { PersonDelegation } from './person-delegation';
import { PersonLogin } from './person-login';

@Component({
  selector: 'person-admin-delegation',
  templateUrl: './person-admin-delegation.component.html',
  styleUrls: ['./person-admin-delegation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButton, MatIcon, MatDatepickerInput, MatDatepickerToggle, MatDatepicker, MatProgressSpinner, DatePipe],
})
export class PersonAdminDelegationComponent implements OnInit {
  readonly personId = input.required<string>({ alias: 'person-id' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly delegationService = inject(TaskDelegationService);
  private readonly selectionService = inject(SelectionService);
  private readonly router = inject(Router);
  readonly personLogin = signal<PersonLogin | undefined>(undefined);
  readonly delegations = signal<PersonDelegation[]>([]);
  readonly allDelegationUsers = signal<SelectionItem[]>([]);
  readonly selectedDelegation = signal<PersonDelegation | undefined>(undefined);
  readonly isBusy = signal<boolean>(false);
  readonly errorMessage = signal<string>('');
  readonly activeDelegations = computed(() => this.delegations().filter((delegation) => this.isActive(delegation)));
  readonly delegationUsers = computed(() => this.allDelegationUsers().filter((user) => user.code !== this.personLogin()?.token));
  readonly personName = computed(() => this.personLogin()?.person?.description ?? '');

  ngOnInit(): void {
    if (this.personLoginService.isAdmin()) {
      this.isBusy.set(true);
      forkJoin({
        personLogin: this.personLoginService.getPersonLogin(this.personId()),
        users: this.selectionService.getDelegationUsers(),
      }).subscribe({
        next: (result) => {
          this.allDelegationUsers.set(result.users ?? []);
          this.personLogin.set(result.personLogin ?? undefined);
          if (result.personLogin) {
            this.loadDelegations();
          } else {
            this.isBusy.set(false);
            this.errorMessage.set('This person does not have a login record');
          }
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
    } else {
      this.router.navigate(['person']);
    }
  }

  formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  setStartTime(newValue: Date): void {
    this.selectedDelegation.update((delegation) => ({ ...delegation!, startTime: DateUtils.formatOutput(newValue) }));
  }

  setEndTime(newValue: Date): void {
    this.selectedDelegation.update((delegation) => ({ ...delegation!, endTime: DateUtils.formatOutput(newValue) }));
  }

  setDelegate(delegate: string): void {
    this.selectedDelegation.update((delegation) => ({ ...delegation!, delegate }));
  }

  isSelected(delegation: PersonDelegation): boolean {
    return this.selectedDelegation()?.id === delegation.id;
  }

  canSave(): boolean {
    return !this.isBusy() && StringUtils.isNotBlank(this.selectedDelegation()?.delegate);
  }

  selectDelegation(delegation: PersonDelegation): void {
    this.selectedDelegation.set({ ...delegation });
  }

  cancelUpdate(): void {
    this.selectedDelegation.set(undefined);
  }

  newDelegation(): void {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const delegation = new PersonDelegation();
    delegation.assignee = this.personLogin()!.token;
    delegation.startTime = DateUtils.formatOutput(new Date());
    delegation.endTime = DateUtils.formatOutput(tomorrow);
    this.selectedDelegation.set(delegation);
  }

  endNow(): void {
    this.selectedDelegation.update((delegation) => ({ ...delegation!, endTime: delegation!.startTime }));
    this.saveDelegation();
  }

  saveDelegation(): void {
    this.isBusy.set(true);
    this.errorMessage.set('');
    this.delegationService.adminSave(this.selectedDelegation()!).subscribe({
      next: () => this.loadDelegations(),
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  backToPersonDetail(): void {
    this.router.navigate(['person/' + this.personId()]);
  }

  private loadDelegations(): void {
    this.isBusy.set(true);
    this.delegationService.getPersonDelegations(this.personId()).subscribe({
      next: (results) => {
        this.delegations.set(results ?? []);
        this.selectedDelegation.set(undefined);
        this.isBusy.set(false);
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  private isActive(delegation: PersonDelegation): boolean {
    const endDate = DateUtils.formatDisplay(delegation.endTime);
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return !!endDate && endDate.getTime() >= tomorrow.getTime();
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }
}
